import { startCase } from 'lodash';
import TagManager from 'react-gtm-module';

import {
    CreateAppointmentMutation, CustomerMembershipTierEnum, StaffAppointmentStatusEnum, VerifyCodeMutation
} from './__generated__/graphql';
import {
    ClientStoreType, PatientStoreType, ExternalClinicStoreType
} from './store';
import { getOperatingSystem } from './utils';

export const DOWNLOAD_APP_LINK = 'https://modernanimal.app.link/lvuB4tquqDb';
// There's a known issue with being unable to open some links with target="_blank"
// on some mobile browsers (i.e. Chrome), notably our DOWNLOAD_APP_LINK. Setting the
// target to "_self" fixes the issue for mobile.
export const HREF_TARGET = getOperatingSystem() === 'Desktop' ? '_blank' : '_self';
export const MOBILE_WIDTH_PX = 736;
export const BREAKPOINTS = { mobile: 0, desktop: MOBILE_WIDTH_PX };

export const getClinicDirectionsUrl = (location: CreateAppointmentMutation['createAppointment']['location']) => {
    const platform = getOperatingSystem();

    if (platform === 'iOS') {
        return `http://maps.apple.com/maps?daddr=${location.latitude},${location.longitude}`;
    }

    return `http://maps.google.com/maps?daddr=${location.latitude},${location.longitude}`;
};

const MID_AND_POST_APPOINTMENT_STATUSES = [
    StaffAppointmentStatusEnum.Planned,
    StaffAppointmentStatusEnum.CheckedIn,
    StaffAppointmentStatusEnum.Admitted,
    StaffAppointmentStatusEnum.Inprogress,
    StaffAppointmentStatusEnum.CheckingOut,
    StaffAppointmentStatusEnum.CheckedOut,
    StaffAppointmentStatusEnum.Completed
];
export const hasScheduledOrCompletedAppointment = (
    client: VerifyCodeMutation['authorizePhone']['client'],
): boolean => {
    if (!client) return false;

    return client.household.patients.some(
        (patient) => patient.appointments.some(
            (appointment) => !!appointment?.status && MID_AND_POST_APPOINTMENT_STATUSES.includes(appointment.status)
        )
    );
};

export type BookingFlowScreenName = 'SelectAppointment' | 'PhoneNumber' | 'PhoneVerification' | 'PatientBasics' | 'PatientAttributes'
    | 'ReasonForVisit' | 'MedicalRecords' | 'ClientOnboarding' | 'AppointmentConfirmation' | 'AddCard' | 'TestClientOnboarding' | 'TestPatientBasics' | 'TestSelectAppointment';

// Standard booking flow screen order (original)
export const STANDARD_BOOKING_FLOW_SCREENS: BookingFlowScreenName[] = [
    'SelectAppointment',
    'PhoneNumber',
    'PhoneVerification',
    'PatientBasics',
    'PatientAttributes',
    'ReasonForVisit',
    'MedicalRecords',
    'ClientOnboarding',
    'AddCard',
    'AppointmentConfirmation'
];

// Test booking flow screen order
export const TEST_BOOKING_FLOW_SCREENS: BookingFlowScreenName[] = [
    'TestClientOnboarding',
    'TestPatientBasics',
    'MedicalRecords',
    'ReasonForVisit',
    'TestSelectAppointment',
    'AddCard',
    'AppointmentConfirmation'
];

export const getLastProgressScreenName = (
    client: ClientStoreType,
    patient: PatientStoreType,
    externalClinic: ExternalClinicStoreType
): BookingFlowScreenName => {
    const hasCompletedClientOnboarding = () => !!client.firstName && !!client.lastName
        && !!client.email && !!client.fullAddress;
    const hasCompletedMedicalRecords = () => !!externalClinic.placeId || patient.recordsAvailability !== null;
    const hasCompletedPatientAttributes = () => !!patient.breed && !!patient.color && !!patient.sex;
    const hasCompletedPatientBasics = () => !!patient.name && !!patient.species && !!patient.birthdate;

    if (hasCompletedClientOnboarding()) return 'AddCard';
    if (hasCompletedMedicalRecords()) return 'ClientOnboarding';
    if (hasCompletedPatientAttributes()) return 'ReasonForVisit';
    if (hasCompletedPatientBasics()) return 'PatientAttributes';
    return 'PatientBasics';
};

// Declare GTM event names as constants for better maintainability
export const GTM_EVENT_CLIENT_ACCOUNT_CREATED = 'client_account_created';

export const pushToGTM = (
    payload: {
        event: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    }
): void => {
    TagManager.dataLayer({ dataLayer: payload });
};

export const trackGTMPurchaseEvent = (
    transactionId: string,
    productId: CustomerMembershipTierEnum,
    price = '0.00'
) => {
    const productName = startCase(productId.replace('_', ' ').toLowerCase());

    pushToGTM({
        event: 'purchase',
        ecommerce: {
            purchase: {
                transaction_id: transactionId,
                value: price,
                currency: 'USD',
                items: [
                    {
                        id: productId,
                        name: productName,
                        category: 'Membership',
                        quantity: 1,
                        price
                    }
                ]
            }
        }
    });
};
